import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { toast } from 'sonner';
import {
	IMeetingReminderTemplate,
	useMeetingReminderTemplatesQuery,
} from '../../../../../queries/AutomationTemplate/useMeetingReminderTemplatesQuery';
import { useMeetingConfigBulkOverrideMutation } from '../../../../../queries/TeamMeetingConfig';
import { Checkbox } from '../../../../components-v2/Checkbox';
import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxItemRadioIndicator,
	ComboboxList,
} from '../../../../components-v2/Combobox';
import { Input } from '../../../../components-v2/Input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../../components-v2/Select';
import { baseStyleSheet } from '../../../../styles/styles';
import { styleSheet } from '../styles';

// Duration options in minutes
const DURATION_OPTIONS = [
	{ value: 15, label: '15 minutes' },
	{ value: 30, label: '30 minutes' },
	{ value: 45, label: '45 minutes' },
	{ value: 60, label: '1 hour' },
	{ value: 90, label: '90 minutes' },
	{ value: 120, label: '2 hours' },
	{ value: 180, label: '3 hours' },
	{ value: 240, label: '4 hours' },
];

// Time slot start options (minutes past the hour)
const SLOT_START_OPTIONS = [
	{ value: 0, label: ':00' },
	{ value: 15, label: ':15' },
	{ value: 30, label: ':30' },
	{ value: 45, label: ':45' },
];

// Input range constraints
const MAX_PER_DAY_RANGE = { min: 1, max: 10 };
const MAX_PER_MONTH_RANGE = { min: 1, max: 50 };
const MAX_DAYS_OUT_RANGE = { min: 1, max: 180 };

// Local state for the form
interface IBulkSettingsFormState {
	durationMinutes: { override: boolean; value: number };
	slotStartInMinutes: { override: boolean; value: number[] };
	maxPerDay: { override: boolean; value: number | null };
	maxPerMonth: { override: boolean; value: number | null };
	atMostIntervalDays: { override: boolean; value: number | null };
	meetingReminderTemplateId: { override: boolean; value: string | null };
}

const DEFAULT_FORM_STATE: IBulkSettingsFormState = {
	durationMinutes: { override: false, value: 30 },
	slotStartInMinutes: { override: false, value: [0, 30] },
	maxPerDay: { override: false, value: 5 },
	maxPerMonth: { override: false, value: 20 },
	atMostIntervalDays: { override: false, value: 30 },
	meetingReminderTemplateId: { override: false, value: null },
};

interface IBulkSettingsOverrideProps {
	isOpen: boolean;
	onClose: () => void;
	selectedMeetingConfigIds: string[];
	onApplySuccess: () => void;
}

function daysToTimeSpan(days: number): string {
	return `${days}.00:00:00`;
}

// Type guard for extracting error messages safely
function getErrorMessage(error: unknown): string {
	if (error && typeof error === 'object') {
		if ('systemMessage' in error && typeof error.systemMessage === 'string') {
			return error.systemMessage;
		}
		if ('message' in error && typeof error.message === 'string') {
			return error.message;
		}
	}
	return 'Unknown error';
}

// Helper to format template label
const getTemplateLabel = (template: IMeetingReminderTemplate | 'None'): string => {
	if (template === 'None') {
		return 'None';
	}
	const creatorName =
		template.creator?.firstName || template.creator?.lastName
			? ` (${template.creator?.firstName ?? ''} ${template.creator?.lastName ?? ''})`.replace(/\s+/g, ' ')
			: '';
	return `${template.name}${creatorName}`;
};

export function BulkSettingsOverride({
	isOpen,
	onClose,
	selectedMeetingConfigIds,
	onApplySuccess,
}: IBulkSettingsOverrideProps) {
	const [settings, setSettings] = React.useState<IBulkSettingsFormState>(DEFAULT_FORM_STATE);

	// Fetch meeting reminder templates
	const { data: reminderTemplates = [], isLoading: isLoadingTemplates } = useMeetingReminderTemplatesQuery();

	// Find the selected template object
	const selectedTemplate = React.useMemo(() => {
		if (!settings.meetingReminderTemplateId.value) return null;
		return reminderTemplates.find(t => t.id === settings.meetingReminderTemplateId.value) ?? null;
	}, [reminderTemplates, settings.meetingReminderTemplateId.value]);

	const bulkOverrideMutation = useMeetingConfigBulkOverrideMutation({
		onSuccess: data => {
			const successCount = data?.succeeded?.length ?? 0;
			const failedCount = data?.failed?.length ?? 0;

			if (failedCount === 0) {
				toast.success(`Successfully updated ${successCount} meeting config${successCount !== 1 ? 's' : ''}`);
			} else if (successCount === 0) {
				toast.error(`Failed to update ${failedCount} meeting config${failedCount !== 1 ? 's' : ''}`);
			} else {
				toast.warning(`Updated ${successCount} config${successCount !== 1 ? 's' : ''}, ${failedCount} failed`);
			}

			onApplySuccess();
			setSettings(DEFAULT_FORM_STATE);
		},
		onError: (error: unknown) => {
			const errorDetails = getErrorMessage(error);
			toast.error(`Failed to apply settings. Error details: ${errorDetails}`);
		},
	});

	// Reset form when panel opens
	React.useEffect(() => {
		if (isOpen) {
			setSettings(DEFAULT_FORM_STATE);
		}
	}, [isOpen]);

	const updateSetting = <K extends keyof IBulkSettingsFormState>(
		key: K,
		update: Partial<IBulkSettingsFormState[K]>
	) => {
		setSettings(prev => ({
			...prev,
			[key]: { ...prev[key], ...update },
		}));
	};

	const toggleSlotStart = (slot: number) => {
		setSettings(prev => {
			const currentSlots = prev.slotStartInMinutes.value;
			const newSlots = currentSlots.includes(slot)
				? currentSlots.filter(s => s !== slot)
				: [...currentSlots, slot].sort((a, b) => a - b);
			return {
				...prev,
				slotStartInMinutes: { ...prev.slotStartInMinutes, value: newSlots },
			};
		});
	};

	const hasAnyOverride =
		settings.durationMinutes.override ||
		settings.slotStartInMinutes.override ||
		settings.maxPerDay.override ||
		settings.maxPerMonth.override ||
		settings.atMostIntervalDays.override ||
		settings.meetingReminderTemplateId.override;

	const isValid = () => {
		// If slot override is enabled, at least one slot must be selected
		if (settings.slotStartInMinutes.override && settings.slotStartInMinutes.value.length === 0) {
			return false;
		}
		return hasAnyOverride;
	};

	const handleApply = () => {
		if (!isValid()) {
			if (settings.slotStartInMinutes.override && settings.slotStartInMinutes.value.length === 0) {
				toast.error('At least one time slot must be selected');
			}
			return;
		}

		const apiSettings: Api.IMeetingConfigOverrideSettings = {};

		if (settings.durationMinutes.override) {
			apiSettings.durationMinutes = {
				override: true,
				value: settings.durationMinutes.value,
			};
		}
		if (settings.slotStartInMinutes.override) {
			apiSettings.slotStartInMinutes = {
				override: true,
				value: settings.slotStartInMinutes.value,
			};
		}
		if (settings.maxPerDay.override) {
			apiSettings.maxPerDay = {
				override: true,
				value: settings.maxPerDay.value,
			};
		}
		if (settings.maxPerMonth.override) {
			apiSettings.maxPerMonth = {
				override: true,
				value: settings.maxPerMonth.value,
			};
		}
		if (settings.atMostIntervalDays.override && settings.atMostIntervalDays.value !== null) {
			apiSettings.atMostInterval = {
				override: true,
				value: daysToTimeSpan(settings.atMostIntervalDays.value),
			};
		}
		if (settings.meetingReminderTemplateId.override) {
			apiSettings.meetingReminderTemplateId = {
				override: true,
				value: settings.meetingReminderTemplateId.value,
			};
		}

		bulkOverrideMutation.mutate({
			meetingConfigIds: selectedMeetingConfigIds,
			settings: apiSettings,
		});
	};

	if (!isOpen) {
		return null;
	}

	return (
		<div className={css(styleSheet.settingsPanel)}>
			<div className={css(styleSheet.settingsPanelHeader)}>
				<h4 className={css(styleSheet.settingsPanelTitle)}>
					Override settings for {selectedMeetingConfigIds.length} selected member
					{selectedMeetingConfigIds.length !== 1 ? 's' : ''}
				</h4>
				<button type='button' className={css(styleSheet.settingsPanelClose)} onClick={onClose} aria-label='Close'>
					&times;
				</button>
			</div>

			{/* Duration */}
			<div className={css(styleSheet.settingRow)}>
				<label className={css(styleSheet.settingLabel)}>
					<Checkbox
						checked={settings.durationMinutes.override}
						onCheckedChange={checked => updateSetting('durationMinutes', { override: checked === true })}
					/>
					<span>Duration</span>
				</label>
				<div
					className={css(
						styleSheet.settingControl,
						!settings.durationMinutes.override && styleSheet.settingControlDisabled
					)}
				>
					<Select
						value={String(settings.durationMinutes.value)}
						onValueChange={v => updateSetting('durationMinutes', { value: Number(v) })}
						disabled={!settings.durationMinutes.override}
					>
						<SelectTrigger styles={styleSheet.selectTrigger}>
							<SelectValue />
						</SelectTrigger>
						<SelectContent>
							{DURATION_OPTIONS.map(opt => (
								<SelectItem key={opt.value} value={String(opt.value)}>
									{opt.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>
			</div>

			{/* Time Slots */}
			<div className={css(styleSheet.settingRow)}>
				<label className={css(styleSheet.settingLabel)}>
					<Checkbox
						checked={settings.slotStartInMinutes.override}
						onCheckedChange={checked => updateSetting('slotStartInMinutes', { override: checked === true })}
					/>
					<span>Time Slots</span>
				</label>
				<div
					className={css(
						styleSheet.slotStartOptions,
						!settings.slotStartInMinutes.override && styleSheet.settingControlDisabled
					)}
				>
					{SLOT_START_OPTIONS.map(opt => (
						<label key={opt.value} className={css(styleSheet.slotStartOption)}>
							<Checkbox
								checked={settings.slotStartInMinutes.value.includes(opt.value)}
								onCheckedChange={() => toggleSlotStart(opt.value)}
								disabled={!settings.slotStartInMinutes.override}
							/>
							<span>{opt.label}</span>
						</label>
					))}
				</div>
			</div>

			{/* Max Per Day */}
			<div className={css(styleSheet.settingRow)}>
				<label className={css(styleSheet.settingLabel)}>
					<Checkbox
						checked={settings.maxPerDay.override}
						onCheckedChange={checked => updateSetting('maxPerDay', { override: checked === true })}
					/>
					<span>Max Per Day</span>
				</label>
				<div
					className={css(styleSheet.limitInputRow, !settings.maxPerDay.override && styleSheet.settingControlDisabled)}
				>
					<Input
						type='number'
						styles={[styleSheet.limitInput]}
						value={settings.maxPerDay.value ?? ''}
						onChange={e => {
							const val = e.target.value ? parseInt(e.target.value, 10) : null;
							updateSetting('maxPerDay', { value: val });
						}}
						disabled={!settings.maxPerDay.override}
						min={MAX_PER_DAY_RANGE.min}
						max={MAX_PER_DAY_RANGE.max}
					/>
				</div>
			</div>

			{/* Max Per Month */}
			<div className={css(styleSheet.settingRow)}>
				<label className={css(styleSheet.settingLabel)}>
					<Checkbox
						checked={settings.maxPerMonth.override}
						onCheckedChange={checked => updateSetting('maxPerMonth', { override: checked === true })}
					/>
					<span>Max Per Month</span>
				</label>
				<div
					className={css(styleSheet.limitInputRow, !settings.maxPerMonth.override && styleSheet.settingControlDisabled)}
				>
					<Input
						type='number'
						styles={[styleSheet.limitInput]}
						value={settings.maxPerMonth.value ?? ''}
						onChange={e => {
							const val = e.target.value ? parseInt(e.target.value, 10) : null;
							updateSetting('maxPerMonth', { value: val });
						}}
						disabled={!settings.maxPerMonth.override}
						min={MAX_PER_MONTH_RANGE.min}
						max={MAX_PER_MONTH_RANGE.max}
					/>
				</div>
			</div>

			{/* Max Days Out (atMostInterval) */}
			<div className={css(styleSheet.settingRow)}>
				<label className={css(styleSheet.settingLabel)}>
					<Checkbox
						checked={settings.atMostIntervalDays.override}
						onCheckedChange={checked => updateSetting('atMostIntervalDays', { override: checked === true })}
					/>
					<span>Max Days Out</span>
				</label>
				<div
					className={css(
						styleSheet.limitInputRow,
						!settings.atMostIntervalDays.override && styleSheet.settingControlDisabled
					)}
				>
					<Input
						type='number'
						styles={[styleSheet.limitInput]}
						value={settings.atMostIntervalDays.value ?? ''}
						onChange={e => {
							const val = e.target.value ? parseInt(e.target.value, 10) : null;
							updateSetting('atMostIntervalDays', { value: val });
						}}
						disabled={!settings.atMostIntervalDays.override}
						min={MAX_DAYS_OUT_RANGE.min}
						max={MAX_DAYS_OUT_RANGE.max}
					/>
				</div>
			</div>

			{/* Meeting Reminder Template */}
			<div className={css(styleSheet.settingRow)}>
				<label className={css(styleSheet.settingLabel)}>
					<Checkbox
						checked={settings.meetingReminderTemplateId.override}
						onCheckedChange={checked => updateSetting('meetingReminderTemplateId', { override: checked === true })}
					/>
					<span>Reminder Template</span>
				</label>
				<div
					className={css(
						styleSheet.settingControlWide,
						!settings.meetingReminderTemplateId.override && styleSheet.settingControlDisabled
					)}
				>
					<Combobox
						items={['None' as const, ...reminderTemplates]}
						value={selectedTemplate ?? 'None'}
						onValueChange={value => {
							if (value === 'None' || !value) {
								updateSetting('meetingReminderTemplateId', { value: null });
							} else {
								updateSetting('meetingReminderTemplateId', { value: (value as IMeetingReminderTemplate).id });
							}
						}}
						itemToStringLabel={item => getTemplateLabel(item as IMeetingReminderTemplate | 'None')}
						isItemEqualToValue={(item, value) => {
							if (value === 'None' && item === 'None') {
								return true;
							}
							return (item as IMeetingReminderTemplate)?.id === (value as IMeetingReminderTemplate)?.id;
						}}
						disabled={!settings.meetingReminderTemplateId.override || isLoadingTemplates}
					>
						<ComboboxInput placeholder='Select a reminder template...' />
						<ComboboxContent>
							<ComboboxEmpty>No templates found</ComboboxEmpty>
							<ComboboxList>
								{(template: IMeetingReminderTemplate | 'None') => (
									<ComboboxItem key={template === 'None' ? 'none' : template.id} value={template}>
										<ComboboxItemRadioIndicator />
										{getTemplateLabel(template)}
									</ComboboxItem>
								)}
							</ComboboxList>
						</ComboboxContent>
					</Combobox>
				</div>
			</div>

			<div className={css(styleSheet.applyButton)}>
				<button
					type='button'
					className={css(baseStyleSheet.ctaButton)}
					onClick={handleApply}
					disabled={!hasAnyOverride || bulkOverrideMutation.isPending}
				>
					{bulkOverrideMutation.isPending ? 'Applying...' : 'Apply to selected users'}
				</button>
			</div>
		</div>
	);
}
